import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './app/providers/AuthProvider';

// Layouts
import { DashboardLayout } from './app/layouts/DashboardLayout';
import { AuthLayout } from './app/layouts/AuthLayout';

// Auth pages
import { LoginPage } from './modules/auth/components/LoginPage';
import { RegisterPage } from './modules/auth/components/RegisterPage';

// Dashboard pages (Admin)
import { DashboardPage } from './app/routes/DashboardPage';
import { CoursesPage } from './modules/courses/components/CoursesPage';
import { CourseDetailPage } from './modules/courses/components/CourseDetailPage';
import { MeetingsPage } from './modules/meetings/components/MeetingsPage';
import { MeetingRoomPage } from './modules/meetings/components/MeetingRoomPage';
import { RecordingsPage } from './modules/recordings/components/RecordingsPage';
import { AITeacherPage } from './modules/ai-teacher/components/AITeacherPage';
import { AcademicsPage } from './modules/academics/components/AcademicsPage';
import { FinancePage } from './modules/finance/components/FinancePage';
import { MarketingPage } from './modules/marketing/components/MarketingPage';
import { MyProfilePage } from './modules/profile/components/MyProfilePage';
import { MySchoolPage } from './modules/school/components/MySchoolPage';
import { SettingsPage } from './modules/settings/components/SettingsPage';
import { TeacherProfilePage } from './modules/teachers/components/TeacherProfilePage';
import { MessagesPage } from './modules/messages/components/MessagesPage';
import { CartDrawer } from './components/cart';

// Lazy load Teacher Platform
const TeacherLayout = lazy(() => import('./components/teacher/TeacherLayout'));
const TeacherDashboard = lazy(() => import('./components/teacher/TeacherDashboard'));
const TeacherCoursesPage = lazy(() => import('./components/teacher/TeacherCoursesPage'));
const TeacherSchedulePage = lazy(() => import('./components/teacher/TeacherSchedulePage'));
const TeacherLibraryPage = lazy(() => import('./components/teacher/TeacherLibraryPage'));
const AdminLibraryPage = lazy(() => import('./modules/admin/components/AdminLibraryPage'));
const AdminSchedulePage = lazy(() => import('./modules/admin/components/AdminSchedulePage'));
const TeacherFinancePage = lazy(() => import('./components/teacher/TeacherFinancePage'));
const TeacherMeetingPage = lazy(() => import('./components/teacher/TeacherMeetingPage'));
const TeacherStudentProfilePage = lazy(() => import('./components/teacher/TeacherStudentProfilePage'));
const TeacherOwnProfilePage = lazy(() => import('./components/teacher/TeacherProfilePage'));

// Lazy load Student Platform
const StudentLayout = lazy(() => import('./components/student/StudentLayout'));
const StudentDashboard = lazy(() => import('./components/student/StudentDashboard'));
const StudentCoursesPage = lazy(() => import('./components/student/StudentCoursesPage'));
const StudentSchedulePage = lazy(() => import('./components/student/StudentSchedulePage'));
const StudentTasksPage = lazy(() => import('./components/student/StudentTasksPage'));
const StudentProfilePage = lazy(() => import('./components/student/StudentProfilePage'));
const StudentLessonPage = lazy(() => import('./components/student/StudentLessonPage'));

// Lazy load Super Admin Platform
const SuperAdminLayout = lazy(() => import('./modules/super-admin/layouts/SuperAdminLayout'));
const SuperAdminDashboard = lazy(() => import('./modules/super-admin/pages/DashboardPage'));
const SuperAdminSchools = lazy(() => import('./modules/super-admin/pages/SchoolsPage'));
const SuperAdminSchoolDetails = lazy(() => import('./modules/super-admin/pages/SchoolDetailsPage'));
const SuperAdminUsers = lazy(() => import('./modules/super-admin/pages/UsersPage'));
const SuperAdminSubscriptions = lazy(() => import('./modules/super-admin/pages/SubscriptionsPage'));
const SuperAdminStorage = lazy(() => import('./modules/super-admin/pages/StoragePage'));
const SuperAdminAuditLogs = lazy(() => import('./modules/super-admin/pages/AuditLogsPage'));
const SuperAdminAdmins = lazy(() => import('./modules/super-admin/pages/SuperAdminsPage'));
const SuperAdminSettings = lazy(() => import('./modules/super-admin/pages/SettingsPage'));
const SuperAdminTranslations = lazy(() => import('./modules/super-admin/pages/TranslationsPage'));
const SuperAdminPricing = lazy(() => import('./modules/super-admin/pages/PricingBuilderPage'));
const SuperAdminBilling = lazy(() => import('./modules/super-admin/pages/BillingPage'));
const SuperAdminStripeConnect = lazy(() => import('./modules/super-admin/pages/StripeConnectPage'));

// Lazy load Checkout
const CheckoutPage = lazy(() => import('./modules/checkout/pages/CheckoutPage'));

// Placeholder pages
const PlaceholderPage: React.FC<{ title: string }> = ({ title }) => (
  <div className="p-8">
    <h1 className="text-2xl font-bold text-gray-900 mb-4">{title}</h1>
    <p className="text-gray-500">This page is under development.</p>
  </div>
);

// Loading fallback
const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center animate-pulse">
        <span className="text-white font-bold text-xl">V</span>
      </div>
      <p className="text-gray-500">Loading...</p>
    </div>
  </div>
);

// Protected route for role-based access
const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  allowedRoles?: string[];
}> = ({ children, allowedRoles }) => {
  const { isAuthenticated, userRole, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check if user has required role
  if (allowedRoles && allowedRoles.length > 0 && userRole) {
    if (!allowedRoles.includes(userRole)) {
      // Redirect to appropriate dashboard based on actual role
      return <Navigate to={getRoleDefaultPath(userRole)} replace />;
    }
  }

  return <>{children}</>;
};

// Get default path for a role
const getRoleDefaultPath = (role: string): string => {
  switch (role) {
    case 'super_admin':
      return '/super-admin';
    case 'admin':
      return '/admin';
    case 'teacher':
      return '/teacher';
    case 'student':
      return '/student';
    default:
      return '/login';
  }
};

// Smart redirect component that routes based on user role
const RoleBasedRedirect: React.FC = () => {
  const { isAuthenticated, userRole, isLoading, user } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If user has schools but role is not determined, default to admin
  // This handles cases where user created a school during registration
  if (!userRole && user?.schools && user.schools.length > 0) {
    return <Navigate to="/admin" replace />;
  }

  // Default to admin if role is still not determined (fallback for school creators)
  return <Navigate to={getRoleDefaultPath(userRole || 'admin')} replace />;
};

function App() {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* ============ PUBLIC ROUTES ============ */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
        </Route>

        {/* ============ ADMIN PLATFORM ============ */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="academics" element={<AcademicsPage />} />
          <Route path="students" element={<PlaceholderPage title="Students" />} />
          <Route path="teachers" element={<PlaceholderPage title="Teachers" />} />
          <Route path="courses" element={<CoursesPage />} />
          <Route path="courses/:courseId" element={<CourseDetailPage />} />
          <Route path="students/:studentId" element={<StudentProfilePage />} />
          <Route path="teachers/:teacherId" element={<TeacherProfilePage />} />
          <Route path="meetings" element={<MeetingsPage />} />
          <Route path="meetings/:meetingId" element={<MeetingRoomPage />} />
          <Route path="schedule" element={<AdminSchedulePage />} />
          <Route path="recordings" element={<RecordingsPage />} />
          <Route path="library" element={<AdminLibraryPage />} />
          <Route path="finance" element={<FinancePage />} />
          <Route path="marketing" element={<MarketingPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="profile" element={<MyProfilePage />} />
          <Route path="school" element={<MySchoolPage />} />
          <Route path="ai-teacher" element={<AITeacherPage />} />
          <Route path="messages" element={<MessagesPage />} />
          <Route path="checkout" element={<CheckoutPage />} />
        </Route>

        {/* ============ TEACHER PLATFORM ============ */}
        <Route
          path="/teacher"
          element={
            <ProtectedRoute allowedRoles={['teacher']}>
              <TeacherLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<TeacherDashboard />} />
          <Route path="courses" element={<TeacherCoursesPage />} />
          <Route path="courses/:courseId" element={<TeacherCoursesPage />} />
          <Route path="schedule" element={<TeacherSchedulePage />} />
          <Route path="library" element={<TeacherLibraryPage />} />
          <Route path="finance" element={<TeacherFinancePage />} />
          <Route path="meeting/:sessionId" element={<TeacherMeetingPage />} />
          <Route path="meetings/:meetingId" element={<MeetingRoomPage />} />
          <Route path="students/:studentId" element={<TeacherStudentProfilePage />} />
          <Route path="profile" element={<TeacherOwnProfilePage />} />
          <Route path="settings" element={<PlaceholderPage title="Settings" />} />
          <Route path="messages" element={<MessagesPage />} />
        </Route>

        {/* ============ STUDENT PLATFORM ============ */}
        <Route
          path="/student"
          element={
            <ProtectedRoute allowedRoles={['student']}>
              <StudentLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<StudentDashboard />} />
          <Route path="courses" element={<StudentCoursesPage />} />
          <Route path="courses/:courseId" element={<StudentCoursesPage />} />
          <Route path="tasks" element={<StudentTasksPage />} />
          <Route path="schedule" element={<StudentSchedulePage />} />
          <Route path="profile" element={<StudentProfilePage />} />
          <Route path="messages" element={<MessagesPage />} />
          <Route path="lesson/:sessionId" element={<StudentLessonPage />} />
          <Route path="meetings/:meetingId" element={<MeetingRoomPage />} />
        </Route>

        {/* ============ SUPER ADMIN PLATFORM ============ */}
        <Route
          path="/super-admin"
          element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SuperAdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<SuperAdminDashboard />} />
          <Route path="schools" element={<SuperAdminSchools />} />
          <Route path="schools/:schoolId" element={<SuperAdminSchoolDetails />} />
          <Route path="users" element={<SuperAdminUsers />} />
          <Route path="subscriptions" element={<SuperAdminSubscriptions />} />
          <Route path="storage" element={<SuperAdminStorage />} />
          <Route path="audit-logs" element={<SuperAdminAuditLogs />} />
          <Route path="admins" element={<SuperAdminAdmins />} />
          <Route path="settings" element={<SuperAdminSettings />} />
          <Route path="translations" element={<SuperAdminTranslations />} />
          <Route path="pricing" element={<SuperAdminPricing />} />
          <Route path="billing" element={<SuperAdminBilling />} />
          <Route path="stripe-connect" element={<SuperAdminStripeConnect />} />
        </Route>

        {/* ============ MEETING ROOM (Legacy Route - redirects to role-based) ============ */}
        <Route
          path="/meeting/:meetingId"
          element={
            isAuthenticated ? <MeetingRoomPage /> : <Navigate to="/login" replace />
          }
        />

        {/* ============ ROOT REDIRECT ============ */}
        {/* Redirect based on user role */}
        <Route path="/" element={<RoleBasedRedirect />} />

        {/* ============ 404 ============ */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <CartDrawer />
    </Suspense>
  );
}

export default App;
